import { useState } from "react";
import { ImageOff } from "lucide-react";
import { resolveFileUrl } from "../../utils/fileUrl";
import { fetchNoticeAttachmentUrl } from "../../controllers/noticeController";

const MAX_TILES = 4;

/** One gallery image. Signed links expire, so a failed load fetches a fresh link once. */
function Tile({ image, onOpen, className = "", overlay }) {
  const [src, setSrc] = useState(image.url);
  const [state, setState] = useState("loading"); // loading | loaded | retrying | failed

  const onError = async () => {
    if (state === "retrying") return setState("failed");
    setState("retrying");
    try {
      setSrc((await fetchNoticeAttachmentUrl(image.id)).url);
    } catch {
      setState("failed");
    }
  };

  return (
    <button
      type="button"
      onClick={() => onOpen(image.id)}
      className={`group relative block overflow-hidden bg-app-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${className}`}
      aria-label={`View ${image.fileName}`}
    >
      {state === "failed" ? (
        <span className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-2 text-app-muted">
          <ImageOff className="h-8 w-8" aria-hidden="true" />
          <span className="text-xs">Couldn&apos;t load image</span>
        </span>
      ) : (
        <img
          src={resolveFileUrl(src)}
          alt={image.fileName}
          loading="lazy"
          draggable={false}
          onLoad={() => setState("loaded")}
          onError={onError}
          className={`h-full w-full object-cover transition duration-300 group-hover:scale-[1.02] group-hover:brightness-95 ${
            state === "loaded" ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
      {state !== "loaded" && state !== "failed" && <span className="absolute inset-0 animate-pulse bg-app-hover" aria-hidden="true" />}
      {overlay}
    </button>
  );
}

/** Social-post style image grid: 1 large, 2 side by side, 3 as one tall + two, 4+ as a 2×2 grid with "+N". */
function NoticeGallery({ images, onOpen }) {
  if (!images?.length) return null;
  const shown = images.slice(0, MAX_TILES);
  const extra = images.length - shown.length;

  if (images.length === 1) {
    return (
      <Tile
        image={images[0]}
        onOpen={onOpen}
        className="max-h-[520px] min-h-[200px] w-full [&>img]:max-h-[520px]"
      />
    );
  }

  const layout =
    images.length === 2
      ? "grid-cols-2 aspect-[2/1]"
      : images.length === 3
        ? "grid-cols-2 grid-rows-2 aspect-[4/3]"
        : "grid-cols-2 grid-rows-2 aspect-square sm:aspect-[4/3]";

  return (
    <div className={`grid gap-0.5 ${layout}`}>
      {shown.map((image, i) => (
        <Tile
          key={image.id}
          image={image}
          onOpen={onOpen}
          className={images.length === 3 && i === 0 ? "row-span-2" : ""}
          overlay={
            extra > 0 && i === shown.length - 1 ? (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-3xl font-semibold text-white">
                +{extra}
              </span>
            ) : null
          }
        />
      ))}
    </div>
  );
}

export default NoticeGallery;
